import api from "./api";

/**
 * User Management Service
 * Handles all user management operations (admin only)
 */

/**
 * Get all users
 * @returns {Promise<Array>} Array of users
 */
export const getAllUsers = async () => {
  try {
    const response = await api.get("/api/user-management/getAllUsers");
    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User data
 */
export const getUserById = async (userId) => {
  try {
    const response = await api.post("/api/user-management/getUserById", {
      userId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

/**
 * Update user information
 * @param {Object} userData - User data to update
 * @param {string} userData.id - User ID
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} userData.email - Email
 * @param {string} userData.role - Role (admin, consultant, user)
 * @returns {Promise<Object>} Updated user data
 */
export const updateUser = async (userData) => {
  try {
    const response = await api.post("/api/user-management/updateUser", {
      data: userData,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

/**
 * Update user role
 * @param {string} userId - User ID
 * @param {string} role - New role (admin, consultant, user)
 * @returns {Promise<Object>} Updated user data
 */
export const updateUserRole = async (userId, role) => {
  try {
    const response = await api.post("/api/user-management/updateUserRole", {
      id: userId,
      role,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
};

/**
 * Assign consultant to customer
 * @param {string} consultantId - Consultant user ID
 * @param {string} customerId - Customer ID
 * @returns {Promise<Object>} Assignment data
 */
export const assignConsultantToCustomer = async (consultantId, customerId) => {
  try {
    const response = await api.post("/api/user-management/assignConsultant", {
      consultantId,
      customerId,
    });
    return response.data;
  } catch (error) {
    console.error("Error assigning consultant:", error);
    throw error;
  }
};

/**
 * Remove consultant from customer
 * @param {string} consultantId - Consultant user ID
 * @param {string} customerId - Customer ID
 * @returns {Promise<Object>} Removal confirmation
 */
export const removeConsultantFromCustomer = async (
  consultantId,
  customerId
) => {
  try {
    const response = await api.post("/api/user-management/removeAssignment", {
      consultantId,
      customerId,
    });
    return response.data;
  } catch (error) {
    console.error("Error removing consultant:", error);
    throw error;
  }
};

/**
 * Get consultant assignments
 * @param {string} consultantId - Consultant user ID
 * @returns {Promise<Array>} Array of customer assignments
 */
export const getConsultantAssignments = async (consultantId) => {
  try {
    const response = await api.post("/api/user-management/getAssignments", {
      consultantId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching consultant assignments:", error);
    throw error;
  }
};

/**
 * Delete user
 * @param {string} userId - User ID to delete
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteUser = async (userId) => {
  try {
    const response = await api.post("/api/user-management/deleteUser", {
      userId,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

export default {
  getAllUsers,
  getUserById,
  updateUser,
  updateUserRole,
  assignConsultantToCustomer,
  removeConsultantFromCustomer,
  getConsultantAssignments,
  deleteUser,
};
